import { ReactNode } from 'react';

import { Badge, Panel, TwoColumnLayout, VerticalContainer } from '@/components';

import { R } from '@/caregiverSpace/resources/Texts.resx';

import {
    IConsultationDetailsDto,
    PropertyObjectType,
    PropertyType,
} from '@/caregiverSpace/shared/model';
import { PropertiesList } from '@/caregiverSpace/shared/ui';

import { AccessViaView } from './AccessViaView';
import { ActiveProgramsView } from './ActiveProgramsView';
import { CouponView } from './CouponView';
import { DeletedProfilePanel } from './DeletedProfilePanel';
import { PatientSection } from './PatientSection';
import { VetPatientSection } from './VetPatientSection';

const getLeftColumn = (
    { Consultation }: IConsultationDetailsDto,
    patientActions?: ReactNode,
    onBehalfPatientActions?: ReactNode,
) => {
    if (Consultation.OnBehalfOfPatient) {
        return (
            <PatientSection
                patientId={Consultation.OnBehalfOfPatient.User.ID}
                consultationId={Consultation.UID}
                dateOfBirth={Consultation.OnBehalfOfPatient.DateOfBirth}
                name={Consultation.OnBehalfOfPatient.User.FullName}
                nationalIdentifier={Consultation.OnBehalfOfPatient.CPR}
            >
                {onBehalfPatientActions}
            </PatientSection>
        );
    }

    if (Consultation.OnBehalfOfPet) {
        return <VetPatientSection consultation={Consultation} />;
    }

    return (
        <PatientSection
            patientId={Consultation.Patient?.User.ID}
            consultationId={Consultation.UID}
            dateOfBirth={Consultation.Patient?.DateOfBirth}
            name={Consultation.Patient?.User.FullName}
            nationalIdentifier={Consultation.Patient?.CPR}
            mobilePhone={Consultation.Patient?.MobilePhone}
        >
            {patientActions}
        </PatientSection>
    );
};

const getRightColumn = (
    {
        Consultation,
        SubscriptionPlans,
        CouponRedemption,
    }: IConsultationDetailsDto,
    shouldHideCouponRedemption: boolean,
) => {
    const properties: PropertyObjectType[] = [];

    if (Consultation.Company) {
        properties.push({
            label: R.AccessVia,
            value: (
                <AccessViaView company={Consultation.Company} className="h-4" />
            ),
            type: PropertyType.CUSTOM,
        });
    }

    if (SubscriptionPlans?.length) {
        properties.push({
            label: R.ActivePrograms,
            value: <ActiveProgramsView subscriptions={SubscriptionPlans} />,
            type: PropertyType.CUSTOM,
            isInline: true,
        });
    }

    if (Consultation.Coupon && !shouldHideCouponRedemption) {
        properties.push({
            label: R.ActiveCoupon,
            value: (
                <CouponView
                    coupon={Consultation.Coupon}
                    couponRedemption={CouponRedemption}
                />
            ),
            type: PropertyType.CUSTOM,
        });
    }

    return (
        <VerticalContainer>
            <PropertiesList properties={properties} />
        </VerticalContainer>
    );
};

type PatientInformationPanelProps = {
    consultationDetails: IConsultationDetailsDto;
    className?: string;
    shouldHideCouponRedemption?: boolean;
    patientActions: ReactNode;
    onBehalfPatientActions: ReactNode;
};

export const PatientInformationPanel = ({
    consultationDetails,
    className,
    shouldHideCouponRedemption = false,
    patientActions,
    onBehalfPatientActions,
}: PatientInformationPanelProps) => {
    const leftColumn = getLeftColumn(
        consultationDetails,
        patientActions,
        onBehalfPatientActions,
    );
    const rightColumn = getRightColumn(
        consultationDetails,
        shouldHideCouponRedemption,
    );

    return (
        <Panel className={className}>
            <VerticalContainer>
                <Badge type="default">{R.PatientInformation}</Badge>
                {consultationDetails.Consultation.IsDeleted && (
                    <>
                        <DeletedProfilePanel
                            className="w-fit"
                            isPet={
                                !!consultationDetails.Consultation.OnBehalfOfPet
                            }
                        />
                        {leftColumn}
                    </>
                )}

                {!consultationDetails.Consultation.IsDeleted && (
                    <TwoColumnLayout
                        leftColumn={leftColumn}
                        rightColumn={rightColumn}
                    />
                )}
            </VerticalContainer>
        </Panel>
    );
};
